<template>
  <div>
    <v-list
      :id="spot.ident"
      dense
      two-line
      data-cy="spot-tile"
      class="list-tile-min-height list-tile-auto-height"
    >
      <v-list-tile
        class="list-tile-no-left-padding"
        :class="{'list-tile-align-start': $vuetify.breakpoint.xs}"
        :to="spotTileRoutingTarget"
        @click="onClick ? onClick() : null"
      >
        <content-selection-checkbox
          v-if="!noContentSelectionPlaceholder"
          :show="
            showContentSelectionCheckbox && spot.published && !spot.closingtime
          "
          :content="spot"
          :class="{'mr-2': $vuetify.breakpoint.smAndUp}"
        />
        <img
          v-if="spot.is_new && showNewBadge"
          class="newbadge"
          style="z-index: 1"
          :src="$store.state.common.newBadgeURL"
          :class="{unprocessed: !spot.processed}"
        >
        <!-- Note: Don't add 'grow-0' here as it looks wrong on iOS.
			       Also don't add 'height: 100%', list-tile would grow on
			       hover in IE11 and Edge when wrapped in 'height: auto'
			       container.
        -->
        <v-layout v-bind="previewLayout">
          <!-- Preview -->
          <div class="my-1">
            <div
              v-if="showPreview"
              class="spot-item clickable"
              style="justify-content: center"
              :class="{
                tvpreview: ctype === 'tv',
                radiopreview: ctype === 'radio',
                xs: $vuetify.breakpoint.xs,
              }"
            >
              <mini-preview-unprocessed
                v-if="!spot.processed"
                :ctype="spot.ctype"
                :closingtime="spot.closingtime"
                :small="$vuetify.breakpoint.sm"
                :medium="$vuetify.breakpoint.mdAndUp"
                :full="$vuetify.breakpoint.xs"
                @click.native.prevent.stop
              />
              <div v-else>
                <mini-preview-audio
                  v-if="spot.ctype === 'radio'"
                  :enabled="spot.processed"
                  :closingtime="spot.closingtime"
                  :file-url="spot.file_url"
                  :small="$vuetify.breakpoint.sm"
                  :medium="$vuetify.breakpoint.mdAndUp"
                  :full="$vuetify.breakpoint.xs"
                  @click.native.prevent.stop
                />
                <span
                  v-if="spot.ctype === 'tv'"
                  style="position: relative"
                >
                  <mini-preview-video
                    :full="$vuetify.breakpoint.xs"
                    :medium="$vuetify.breakpoint.mdAndUp"
                    :small="$vuetify.breakpoint.sm"
                    :spot="spot"
                    :tvformat="spot.tvformat"
                    :use-inline-video-player="$vuetify.breakpoint.xs"
                    allow-html-preview
                    @started="videoPreviewRunning = true"
                    @stopped="videoPreviewRunning = false"
                  />
                  <span
                    v-if="
                      showSocialMediaIcons &&
                        spot.tvformat &&
                        spot.tvformat.is_socialmedia_format
                    "
                  >
                    <img
                      v-for="(platformIconUrl, platformIconIndex) in spot
                        .tvformat.socialmedia_icon_urls"
                      :key="platformIconUrl"
                      :src="platformIconUrl"
                      width="20"
                      height="20"
                      style="position: absolute; left: -12px"
                      :style="{top: -6 + platformIconIndex * 20 + 'px'}"
                    >
                  </span>
                </span>
              </div>
            </div>
          </div>
          <!-- Category + Title + Date -->
          <v-layout
            column
            class="grow-3"
            :class="{unprocessed: !spot.processed}"
          >
            <div
              v-if="showCategory"
              class="spot-item category grey--text text--darken-1"
            >
              <span v-if="spot.category">{{ spot.category.name }}</span>
            </div>
            <!-- Title -->
            <div class="spot-item mb-1">
              <v-layout>
                <multiformat-bundle-indicator
                  v-if="hasMultipleFormats"
                  is-multiformat-bundle
                  class="mr-2"
                />
                <span
                  class="spot-title"
                  :class="{
                    'font-weight-bold': hasMultipleFormats,
                    'msr--text':
                      highlightTitle ||
                      (highlightTitleWhenSelected && spot.selected),
                  }"
                  style="font-size: 1.33em; pointer-events: none"
                >{{ overrideTitle || spot.title }}</span>
                <!-- Indicators & Actions -->
                <div
                  v-if="showIndicators"
                  class="ml-auto pl-2"
                >
                  <v-layout>
                    <component
                      :is="$vuetify.breakpoint.smAndUp ? 'table' : 'v-layout'"
                      class="spot-indicators"
                      nowrap
                    >
                      <tr>
                        <td>
                          <unpublished-indicator
                            :is-unpublished="!spot.published"
                          />
                        </td>
                        <td>
                          <from-template-indicator
                            :is-from-template="!!spot.template"
                          />
                        </td>
                        <td>
                          <overlay-options-indicator v-if="hasOverlayOptions" />
                        </td>
                        <td>
                          <on-air-indicator
                            :is-on-air="
                              overrideIsOnAir !== null
                                ? overrideIsOnAir
                                : spot.is_on_air
                            "
                            :is-planned-to-air="spot.is_planned_to_air"
                            data-cy="spot-on-air-indicator"
                          />
                        </td>
                      </tr>
                    </component>
                    <div class="spot-item actions">
                      <slot name="actions" />
                    </div>
                  </v-layout>
                </div>
              </v-layout>
              <!-- Tags -->
              <tag-list
                v-if="spot.tags && spot.tags.length > 0"
                class="mt-1"
                :tags="spot.tags"
              />
              <!-- Customize content overlay_data on User-Decoders it runs on -->
              <div v-if="showEditOverlayDataShortcutButton">
                <overlay-data-chip
                  :title="$t('customizeOverlayDataHintOnActivePlayers')"
                  :label="$t('editDynamicOverlayFieldsOnActivePlayers')"
                  class="spot-title--no-hover-effect"
                  @click.native.prevent="
                    goToContentEditOverlayDataForActivePlayers()
                  "
                />
              </div>
            </div>
            <!-- Subtitle slot -->
            <div class="spot-item grey--text text--darken-1">
              <slot name="subtitle" />
            </div>
            <!-- Date / Author -->
            <div
              v-if="showDate || showAuthor"
              class="spot-item category grey--text text--darken-1"
            >
              <v-layout align-center>
                <span
                  v-if="showDate && !!spot.date"
                >{{ spot.date | dayjs('DD.MM.YYYY') }}&nbsp;</span>
                <span
                  v-if="showAuthor && !!spot.author"
                >{{ $t('from').toLowerCase() }}&nbsp;</span>
                <span v-if="showAuthor && !!spot.author">{{
                  spot.author.label
                }}</span>
                <img
                  v-if="showAuthor && !!spot.author && !!spot.author.image"
                  :src="spot.author.image"
                  class="ml-2"
                  style="object-fit: contain"
                  :style="{
                    'max-height': spot.author.is_group ? '48px' : '32px',
                    'max-width': spot.author.is_group ? '96px' : '64px',
                    'border-radius': spot.author.is_group ? 0 : '50%',
                  }"
                >
              </v-layout>
            </div>
            <!-- Expired -->
            <div
              v-if="showExpiredWarning && spot.expired"
              class="warning--text"
            >
              {{ $t('expiredContentShort') }}
            </div>
          </v-layout>
        </v-layout>
      </v-list-tile>
    </v-list>
    <v-divider v-if="showDivider" />
  </div>
</template>

<script>
import overlayOptions from '@/mixins/overlayOptions.js';

import ContentSelectionCheckbox from '@/components/ContentSelectionCheckbox';
import FromTemplateIndicator from '@/components/FromTemplateIndicator';
import MiniPreviewAudio from '@/components/MiniPreviewAudio';
import MiniPreviewUnprocessed from '@/components/MiniPreviewUnprocessed';
import MiniPreviewVideo from '@/components/MiniPreviewVideo';
import MultiformatBundleIndicator from '@/components/MultiformatBundleIndicator';
import OnAirIndicator from '@/components/OnAirIndicator';
import OverlayDataChip from '@/components/OverlayDataChip';
import OverlayOptionsIndicator from '@/components/OverlayOptionsIndicator';
import TagList from '@/components/TagList';
import UnpublishedIndicator from '@/components/UnpublishedIndicator';

export default {
  components: {
    ContentSelectionCheckbox,
    FromTemplateIndicator,
    MiniPreviewAudio,
    MiniPreviewUnprocessed,
    MiniPreviewVideo,
    MultiformatBundleIndicator,
    OnAirIndicator,
    OverlayDataChip,
    OverlayOptionsIndicator,
    TagList,
    UnpublishedIndicator,
  },
  mixins: [overlayOptions],
  props: {
    /* A Radio- or TVSpot object. */
    spot: {
      type: Object,
      required: true,
    },
    /* 'radio' or 'tv' */
    ctype: {
      type: String,
      required: true,
    },
    overrideTitle: {
      type: String,
      default: null,
    },
    overrideIsOnAir: {
      type: Boolean,
      default: null,
    },
    /* Optional click handler when the whole list-tile is clicked. */
    onClick: {
      type: Function,
      default: null,
    },
    /**
     * Optional override of the spotId to navigate in the default click handler.
     *
     * Used to replace this with the spot.bundle_id from a wrapping
     * TVSpotBundle component, while presercing the native-anchor
     * behavior via v-list-tile's :to prop (e.g. we want to be able to
     * open in a new tab via middle mouse button).
     */
    onClickSpotId: {
      type: Number,
      default: null,
    },
    highlightTitle: {
      type: Boolean,
      default: false,
    },
    highlightTitleWhenSelected: {
      type: Boolean,
      default: true,
    },
    /* Props below control column/widget visibility. */
    showPreview: {
      type: Boolean,
      default: true,
    },
    showCategory: {
      type: Boolean,
      default: true,
    },
    showDate: {
      type: Boolean,
      default: false,
    },
    showAuthor: {
      type: Boolean,
      default: true,
    },
    showExpiredWarning: {
      type: Boolean,
      default() {
        return this.$store.getters.userCanSeeContentStoreStatistics;
      },
    },
    showNewBadge: {
      type: Boolean,
      default: true,
    },
    showDivider: {
      type: Boolean,
      default: true,
    },
    showIndicators: {
      type: Boolean,
      default: true,
    },
    showSocialMediaIcons: {
      type: Boolean,
      default: true,
    },
    showContentSelectionCheckbox: {
      type: Boolean,
      default: false,
    },
    /**
     * Will only have an effect if Spot has overlay_options (aka is
     * editable) and active/planned on at least one User Decoder.
     */
    enableEditOverlayDataShortcut: {
      type: Boolean,
      default: false,
    },
    /** If you want no checkbox and no placeholder for more horizontal space. */
    noContentSelectionPlaceholder: {
      type: Boolean,
      default: false,
    },
    hasMultipleFormats: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      videoPreviewRunning: false,
    };
  },
  computed: {
    showEditOverlayDataShortcutButton() {
      return (
        this.enableEditOverlayDataShortcut &&
        this.spot.ctype == 'tv' &&
        this.hasOverlayOptions &&
        !!this.spot.editable_template &&
        (this.spot.is_on_air || this.spot.is_planned_to_air)
      );
    },
    spotTileRoutingTarget() {
      // Basically deactivate the :to prop, instead we'll rely on @click.
      if (this.onClick) {
        return null;
      }

      // The target content ID may differ (TVSpot VS its bundle).
      return {
        name: 'spot',
        params: {
          ctype: this.ctype,
          spotId: this.onClickSpotId ? this.onClickSpotId : this.spot.id,
        },
      };
    },
    previewLayout() {
      if (this.$vuetify.breakpoint.smAndUp) {
        return {row: true};
      }
      return {column: true};
    },
  },
  methods: {
    goToContentEditOverlayDataForActivePlayers() {
      // Go to TVSpot detail page, the query will open the dialog.
      this.$router.push({
        name: 'spot',
        query: {
          overlayediting: true,
        },
        params: {
          ctype: 'tv',
          spotId: this.onClickSpotId ? this.onClickSpotId : this.spot.id,
        },
      });
    },
  },
};
</script>

<style scoped>
.spot-item .spot-title {
  text-decoration: none;
}
.spot-item:not(:has(.spot-title--no-hover-effect:hover)):hover .spot-title {
  text-decoration: underline;
}

.unprocessed {
  opacity: 0.5;
}

.spot-item {
  word-break: break-word;
}
.spot-item.actions {
  justify-content: flex-end;
}

.category {
  min-width: 80px;
}

.tvpreview {
  display: flex;
  align-items: flex-start;
  margin-right: 1.5em;
}
.tvpreview.xs {
  margin-right: 0;
}

.radiopreview {
  display: flex;
  align-items: flex-start;
  margin-right: 1.5em;
}
.radiopreview.xs {
  margin-right: 0;
}

/* Some hacks below to make indicators a table on larger screens and a
   simpler non-wrapping horizontal layout on smaller ones. */
.spot-indicators td {
  padding: 0 !important;
}
table.spot-indicators td {
  min-width: 24px;
}
.layout.spot-indicators tr {
  display: flex;
  flex-flow: row nowrap;
  align-items: center;
}
</style>



// WEBPACK FOOTER //
// src/components/Spot.vue